// Catalog position trees: pre-generated opening explorer data (plus engine evaluations) for the
// default lines, so a practice needs neither a Lichess account nor a running engine.
// Pure functions only; the file format is produced by scripts/catalog/build-trees.mjs.

/** A move as the generator stores it: [uci, san, white, draws, black, cp, hasChild]. */
export type TreeMove = [string, string, number, number, number, number | null, 0 | 1];

export interface TreeNode {
    n: number;                              // games that reached this position
    op?: string;                            // opening name, when the book knows one
    best?: [string, string, number];        // [uci, san, cp] according to the engine
    m: TreeMove[];
}

export interface Tree {
    nodes: Record<string, TreeNode>;
}

/** The shape the Lichess explorer answers with, so the rest of the app cannot tell the difference. */
export interface ExplorerMove {
    uci: string;
    san: string;
    white: number;
    draws: number;
    black: number;
}

/** Position identity shared with the generator: the first four FEN fields (EPD). */
export function fenKey(fen: string): string {
    return fen.split(" ").slice(0, 4).join(" ");
}

export function lookup(tree: Tree | null | undefined, fen: string | null | undefined): TreeNode | null {
    if (!tree || !tree.nodes || !fen) return null;
    return tree.nodes[fenKey(fen)] || null;
}

/** Same shape the Lichess explorer answers with, so the rest of the app cannot tell the difference. */
export function toExplorerMoves(node: TreeNode | null): ExplorerMove[] {
    if (!node) return [];
    return node.m.map((m) => ({ uci: m[0], san: m[1], white: m[2], draws: m[3], black: m[4] }));
}

export function findMove(node: TreeNode | null, uci: string):
        (ExplorerMove & { cp: number | null; hasChild: boolean }) | null {
    if (!node) return null;
    const m = node.m.find((entry) => entry[0] === uci);
    return m ? { uci: m[0], san: m[1], white: m[2], draws: m[3], black: m[4], cp: m[5], hasChild: m[6] === 1 } : null;
}

export function shareOf(node: TreeNode | null, uci: string): number {
    const total = node ? node.m.reduce((sum, m) => sum + m[2] + m[3] + m[4], 0) : 0;
    const move = findMove(node, uci);
    return total && move ? (move.white + move.draws + move.black) / total : 0;
}

/**
 * Replies the sparring partner may choose from. Only moves that stay inside the tree, so it never
 * walks the practice into a position it knows nothing about; empty when the book ends here.
 */
export function sparringReplies(node: TreeNode | null): ExplorerMove[] {
    if (!node) return [];
    return node.m.filter((m) => m[6] === 1)
        .map((m) => ({ uci: m[0], san: m[1], white: m[2], draws: m[3], black: m[4] }));
}

/** Picks a move with probability proportional to how often it is played. */
export function pickWeighted<T extends { white: number; draws: number; black: number }>(
        moves: T[], random: () => number = Math.random): T | null {
    if (!moves.length) return null;
    const weights = moves.map((m) => m.white + m.draws + m.black);
    const total = weights.reduce((sum, w) => sum + w, 0);
    if (!total) return moves[0]!;
    let roll = random() * total;
    for (let i = 0; i < moves.length; i++) {
        roll -= weights[i]!;
        if (roll <= 0) return moves[i]!;
    }
    return moves[moves.length - 1]!;
}

/** The move to hint at: the soundest of the moves people actually play here. */
export function hintMove(node: TreeNode | null | undefined): { uci: string; san: string } | null {
    if (!node) return null;
    const rated = node.m.filter((m) => m[5] !== null && m[5] !== undefined);
    if (!rated.length) return node.m.length ? { uci: node.m[0]![0], san: node.m[0]![1] } : null;
    const best = rated.reduce((top, m) => (m[5]! > top[5]! ? m : top));
    return { uci: best[0], san: best[1] };
}
